import { AvatarStack, DealOutGrid, EmptyStatePage, GlassCard } from "@/components/application/empty-state-page";

type Tone = "blue" | "success" | "indigo" | "orange";

const TONES: Record<Tone, { pill: string; dot: string }> = {
  blue: { pill: "bg-blue-500/45 text-white ring-blue-300/50", dot: "bg-blue-200" },
  success: { pill: "bg-success-500/45 text-white ring-success-300/50", dot: "bg-success-200" },
  indigo: { pill: "bg-indigo-500/45 text-white ring-indigo-300/50", dot: "bg-indigo-200" },
  orange: { pill: "bg-orange-500/45 text-white ring-orange-300/50", dot: "bg-orange-200" },
};

const COHORTS: { label: string; tone: Tone; count: number; initials: string[]; overflow: number }[] = [
  { label: "Rising Star", tone: "blue", count: 100, initials: ["AM", "JL", "RC"], overflow: 97 },
  { label: "Innovator", tone: "success", count: 44, initials: ["TS", "KD", "MO"], overflow: 41 },
  { label: "Trailblazer", tone: "indigo", count: 80, initials: ["PN", "BW", "EF"], overflow: 77 },
  { label: "Pioneer", tone: "orange", count: 7, initials: ["LH", "GR", "SV"], overflow: 4 },
];

type CohortsEmptyStateProps = {
  onCreate: () => void;
};

export function CohortsEmptyState({ onCreate }: CohortsEmptyStateProps) {
  return (
    <EmptyStatePage
      eyebrow="Cohorts"
      headline={
        <>
          Group creators by
          <br />
          what matters to you
        </>
      }
      subline="Name a group and add the creators who fit, by GMV, followers, engagement, or however you define it. No spreadsheets, no guesswork."
      measureCh={44}
      portraitSrc="/empty-states/portrait-cohorts.webp"
      portraitMaskLeft="22%"
      actionsDelaySeconds={1.91}
      primaryAction={{ label: "Create a cohort", onClick: onCreate }}
      device={
        <DealOutGrid count={COHORTS.length} className="max-w-[680px] grid-cols-4">
          {(index, { dealt, blurDelayMs }) => {
            const cohort = COHORTS[index];
            const tone = TONES[cohort.tone];
            return (
              <GlassCard
                radius="2xl"
                className="h-full"
                style={{
                  backdropFilter: dealt ? "blur(64px)" : "blur(0px)",
                  WebkitBackdropFilter: dealt ? "blur(64px)" : "blur(0px)",
                  transition: `backdrop-filter 520ms ease-out ${blurDelayMs}ms, -webkit-backdrop-filter 520ms ease-out ${blurDelayMs}ms`,
                }}
              >
                <div className="flex flex-col items-start gap-3 px-4 py-4">
                  <span
                    className={`inline-flex items-center gap-2 rounded-full py-1 pr-3 pl-2 text-[13px] font-semibold ring-1 ring-inset ${tone.pill}`}
                  >
                    <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${tone.dot}`} />
                    {cohort.label}
                  </span>
                  <span className="text-sm text-brand-100">
                    <span className="font-semibold text-white tabular-nums">{cohort.count}</span> creators assigned
                  </span>
                  <AvatarStack initials={cohort.initials} overflow={cohort.overflow} />
                </div>
              </GlassCard>
            );
          }}
        </DealOutGrid>
      }
    />
  );
}
